import { WEB_DOCS, WebDocName } from '~/constants/docs.ts';
import { TDocument, UboOwnerMap } from './types';
import { WEB_FORMS_NAME } from '~/modules/identification/constants.ts';
import { TWebDocType } from '~/queries/types';
import { DATE_CHANGE_ADDITIONAL_VERIFICATION_APPLICATION } from '~/modules/identification/forms/Documents/constants.tsx';

export const mapDocName = (
  isLegalResident: boolean,
  type?: { id: number; name: string },
  doc?: TDocument,
  blockId?: number
): string => {
  const isPersonalDoc = [
    WEB_DOCS[WebDocName.personalDataInformationAgreement],
    WEB_DOCS[WebDocName.foreignPassport],
    WEB_DOCS[WebDocName.taxpayerIdentificationNumber],
    WEB_DOCS[WebDocName.passport],
  ].includes(type?.id || -1);

  const shouldAddLabel =
    isLegalResident && isPersonalDoc && ((blockId && blockId === 2) || !blockId);

  if (!shouldAddLabel) {
    return type?.name || '';
  } else {
    return (
      type?.name + (doc && doc.inn ? ` (${doc.inn})` : '') + (doc && doc.pib ? ` - ${doc.pib}` : '')
    );
  }
};

export const getMessageValidBeforeSigning = (
  missingForms: string[],
  missingDocuments: number[],
  webDocs: TWebDocType[]
) => {
  const DOC_NAMES = webDocs.reduce(
    (acc, doc) => {
      acc[doc.id] = doc.name;
      return acc;
    },
    {} as Record<string, string>
  );

  const forms = missingForms
    .filter((form) => form in WEB_FORMS_NAME)
    .map((form) => WEB_FORMS_NAME[form] as string);

  const documents = missingDocuments
    .filter((doc) => doc in DOC_NAMES)
    .map((doc) => DOC_NAMES[doc] as string);

  return { forms, documents };
};

export const getOwnerFullName = (owner: {
  lastName?: string | null;
  firstName?: string | null;
  middleName?: string | null;
}) => [owner.lastName, owner.firstName, owner.middleName].filter(Boolean).join(' ');

export const checkDateAdditionalVerificationApplication = () => {
  const today = new Date();

  today.setHours(0, 0, 0, 0);
  DATE_CHANGE_ADDITIONAL_VERIFICATION_APPLICATION.setHours(0, 0, 0, 0);

  return today >= DATE_CHANGE_ADDITIONAL_VERIFICATION_APPLICATION;
};

export const getFileInputId = (document: TDocument, blockId: number): string => {
  if (blockId === 3) return `fileInput-${blockId}-${document.id}`;

  const key = document.ownerId
    ? `${document.idDictWebDocument}-${document.ownerId}`
    : document.idDictWebDocument;

  return `fileInput-${blockId}-${key}`;
};

const STORAGE_KEY_PREFIX = 'ubo-doc-owner-map';

const getStorageKey = (applicationId: number | string) => `${STORAGE_KEY_PREFIX}-${applicationId}`;

export const getUboOwnerMap = (applicationId: number | string): UboOwnerMap => {
  try {
    const raw = localStorage.getItem(getStorageKey(applicationId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const setUboOwnerMapEntry = (
  applicationId: number | string,
  idDictWebDocument: number,
  idDoc: number,
  ownerId: string
) => {
  const map = getUboOwnerMap(applicationId);
  map[`${idDictWebDocument}:${idDoc}`] = ownerId;
  try {
    localStorage.setItem(getStorageKey(applicationId), JSON.stringify(map));
  } catch {}
};

export const getOwnerIdForDoc = (
  applicationId: number | string,
  idDictWebDocument: number,
  idDoc: number
): string | undefined => getUboOwnerMap(applicationId)[`${idDictWebDocument}:${idDoc}`];
